import {ColumnDefinition, TPTaxReportData} from "src/models/reports/TPTaxReport";
import React from "react";
import ServiceCollection from "src/services/ServiceCollection";
import {TP_TAX_REPORT_FORMATTING_EXCLUSION_SET, TP_TAX_REPORT_SPECIAL_FORMATTING_SET} from "src/utils/constants";
import {useCollection} from "@amzn/awsui-collection-hooks";
import Table from "@amzn/awsui-components-react/polaris/table";

export default function TPTaxReportTableGrid(props: {
    data: TPTaxReportData[],
    services: ServiceCollection,
    columnHeaders: string[],
    loadingStatus: boolean,
    // Optional per-column header label overrides keyed by CLI. When a CLI is present,
    // its display text is used instead of the raw CLI id (e.g. "<cli> (USD)").
    // Cell lookups still use the raw CLI id, so only the header text changes.
    columnHeaderLabels?: { [cli: string]: string }
    // Optional override for the leading Attribute Name cell, used by the LEF segment drill-down to
    // render a segment's code and description separately. Omitted everywhere else, so MAR, CWB and
    // the Account view keep rendering the raw displayName.
    renderAttributeName?: (item: TPTaxReportData) => React.ReactNode
    // Pins the column-header row while the body scrolls. Opt-in, and off by default, because a
    // segment drill-down view is several times taller than the account view it replaces — MAR, CWB and
    // the flag-off LEF report keep scrolling their headers away exactly as before.
    stickyHeader?: boolean
}) {

    const {data = [], services, columnHeaders, columnHeaderLabels, renderAttributeName} = props;

    function formatAttributeValue(attributeName: string, attributeValue: any): string {
        if (!attributeValue) {
            return attributeValue;
        } else if (TP_TAX_REPORT_FORMATTING_EXCLUSION_SET.has(attributeName)) {
            return attributeValue
        } else if (TP_TAX_REPORT_SPECIAL_FORMATTING_SET.has(attributeName)) {
            return services.formattingService.formatString(attributeValue, true, 2);
        }
        return isNaN(attributeValue) ? attributeValue :
            services.formattingService.formatString(attributeValue, false);
    }

    function fetchColumnDefinitions(columnHeaders: string[]): ColumnDefinition[] {
        let columnDefinitions: ColumnDefinition[] = [];
        columnDefinitions.push({
            id: 'attributeName',
            header: 'Attribute Name',
            cell: (item: any) => renderAttributeName ? renderAttributeName(item) : item.displayName,
        });

        columnHeaders.forEach(columnHeader => {
            columnDefinitions.push({
                id: columnHeader,
                header: columnHeaderLabels?.[columnHeader] ?? columnHeader,
                cell: (item: any) => <div style={{ textAlign: 'right' }}>
                    {formatAttributeValue(item?.displayName, item?.attributeValue[columnHeader])}
                </div>,
            })
        })

        return columnDefinitions;
    }

    const {items: rootItems, collectionProps} = useCollection(data, {
        expandableRows: {
            getId: (item) => item.attributeNameHash,
            getParentId: (item) => item.attributeParentNameHash ?? null,
        }
    });

    return (
        <Table
            items={rootItems}
            columnDefinitions={fetchColumnDefinitions(columnHeaders)}
            {...collectionProps}
            loading={props.loadingStatus}
            loadingText={"Loading..."}
            stickyHeader={props.stickyHeader ?? false}
            stickyColumns={
                {
                    first: 1, // First column is Attribute Name, which needs to be sticky in CWB and LEF report
                    last: 0
                }
            }
        />
    );
}